// Bank statement CSV parsing for SA banks.
// We support a generic Date/Description/Amount layout plus per-bank presets
// for the five major SA banks. The user can also remap columns manually.

export interface ParsedRow {
  date: string;            // ISO yyyy-mm-dd
  description: string;
  reference: string | null;
  amount: number;          // signed: positive = money in
  balance_after: number | null;
  external_id: string | null;
  raw: string;
}

export type BankPreset = 'fnb' | 'standard' | 'absa' | 'nedbank' | 'capitec' | 'generic';

/** Best-effort sniffing of which preset to apply, based on the header row. */
export function detectPreset(headers: string[]): BankPreset {
  const h = headers.map((x) => x.toLowerCase().trim());
  if (h.includes('amount') && h.includes('balance') && h.some((s) => s.includes('cheque'))) return 'fnb';
  if (h.some((s) => /standard bank/i.test(s))) return 'standard';
  if (h.some((s) => /absa/i.test(s))) return 'absa';
  if (h.some((s) => /nedbank/i.test(s))) return 'nedbank';
  if (h.some((s) => /capitec/i.test(s))) return 'capitec';
  return 'generic';
}

function splitCsvLine(line: string): string[] {
  // Minimal RFC-4180-ish CSV split (handles quoted fields with commas).
  const out: string[] = [];
  let cur = '';
  let inQ = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQ) {
      if (ch === '"') {
        if (line[i + 1] === '"') { cur += '"'; i++; } else { inQ = false; }
      } else cur += ch;
    } else {
      if (ch === ',') { out.push(cur); cur = ''; }
      else if (ch === '"') inQ = true;
      else cur += ch;
    }
  }
  out.push(cur);
  return out;
}

function parseDate(value: string): string | null {
  const v = value.trim();
  if (!v) return null;
  // Try yyyy-mm-dd, yyyy/mm/dd, dd/mm/yyyy, dd-mm-yyyy, dd Mon yyyy.
  const isoLike = /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/.exec(v);
  if (isoLike) {
    const [, y, m, d] = isoLike;
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }
  const dmy = /^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/.exec(v);
  if (dmy) {
    const [, d, m, y] = dmy;
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }
  const d = new Date(v);
  if (!Number.isNaN(d.getTime())) {
    const yy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yy}-${mm}-${dd}`;
  }
  return null;
}

function parseAmount(value: string): number {
  const cleaned = value.replace(/[Rr\s]/g, '').replace(/,(\d{3})/g, '$1').replace(/,(\d{1,2})$/, '.$1');
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : 0;
}

interface ColumnMap {
  date: number;
  description: number;
  amount: number;
  debit?: number;
  credit?: number;
  balance?: number;
  reference?: number;
  external_id?: number;
}

function presetColumns(preset: BankPreset, headers: string[]): ColumnMap | null {
  const lower = headers.map((h) => h.toLowerCase().trim());
  const idx = (...names: string[]) => {
    for (const n of names) {
      const i = lower.findIndex((h) => h.includes(n));
      if (i >= 0) return i;
    }
    return -1;
  };
  const date = idx('date');
  const description = idx('description', 'narrative', 'transaction', 'details');
  const reference = idx('reference', 'ref ');
  const balance = idx('balance');
  const external = idx('id', 'unique', 'transaction id');
  // Split debit/credit columns?
  const debit = idx('debit');
  const credit = idx('credit');
  const amount = idx('amount', 'value');
  if (date < 0 || description < 0) return null;
  return {
    date,
    description,
    amount: amount < 0 ? -1 : amount,
    debit: debit < 0 ? undefined : debit,
    credit: credit < 0 ? undefined : credit,
    balance: balance < 0 ? undefined : balance,
    reference: reference < 0 ? undefined : reference,
    external_id: external < 0 ? undefined : external,
  };
}

export function parseBankCsv(csv: string, preset: BankPreset = 'generic'): { rows: ParsedRow[]; preset: BankPreset; errors: string[] } {
  const lines = csv.split(/\r?\n/).filter((l) => l.trim().length > 0);
  if (lines.length < 2) return { rows: [], preset, errors: ['CSV has fewer than 2 lines.'] };
  const headers = splitCsvLine(lines[0]);
  const detected = preset === 'generic' ? detectPreset(headers) : preset;
  const map = presetColumns(detected, headers);
  if (!map) return { rows: [], preset: detected, errors: ['Could not detect Date / Description / Amount columns.'] };

  const rows: ParsedRow[] = [];
  const errors: string[] = [];
  for (let i = 1; i < lines.length; i++) {
    const cols = splitCsvLine(lines[i]);
    const date = parseDate(cols[map.date] ?? '');
    if (!date) { errors.push(`Row ${i + 1}: bad date "${cols[map.date]}"`); continue; }
    const description = (cols[map.description] ?? '').trim();
    let amount = 0;
    if (map.debit !== undefined || map.credit !== undefined) {
      const dr = map.debit !== undefined ? parseAmount(cols[map.debit] ?? '') : 0;
      const cr = map.credit !== undefined ? parseAmount(cols[map.credit] ?? '') : 0;
      amount = cr - dr; // money in (credit) is positive
    } else if (map.amount >= 0) {
      amount = parseAmount(cols[map.amount] ?? '');
    }
    const balance_after = map.balance !== undefined ? parseAmount(cols[map.balance] ?? '') : null;
    const reference = map.reference !== undefined ? (cols[map.reference] ?? '').trim() || null : null;
    const external_id = map.external_id !== undefined ? (cols[map.external_id] ?? '').trim() || null : null;
    rows.push({
      date,
      description,
      reference,
      amount,
      balance_after: Number.isFinite(balance_after as number) ? (balance_after as number) : null,
      external_id,
      raw: lines[i],
    });
  }
  return { rows, preset: detected, errors };
}
